import React, { useState, useEffect } from "react";
import { Form, Input, Button, Modal, message } from "antd";
import {
  UserOutlined,
  LockOutlined,
  CloudOutlined,
  SyncOutlined,
  ExclamationCircleOutlined,
  MailOutlined,
  LinkOutlined,
  BankOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import { useAuth } from "../contexts/AuthContext";
import { apiService, Entitlement } from "../services/apiService";
import { Auth } from "aws-amplify";
import "./Settings.css";

const { Password } = Input;

const AWS_MARKETPLACE_URL =
  process.env.REACT_APP_AWS_MARKETPLACE_URL ||
  "https://aws.amazon.com/marketplace/pp/prodview-wmywqkxps32au";

/* ---- Plan tier config ---- */
interface PlanStyle {
  gradient: string;
  badge: string;
}
const PLAN_STYLES: Record<string, PlanStyle> = {
  "Free Trial": {
    gradient: "linear-gradient(135deg, #64748b 0%, #475569 100%)",
    badge: "✦",
  },
  Basic: {
    gradient: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
    badge: "◆",
  },
  Standard: {
    gradient: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
    badge: "⬡",
  },
  Premium: {
    gradient: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
    badge: "★",
  },
};
const FALLBACK_PLAN_STYLE: PlanStyle = {
  gradient: "linear-gradient(135deg, #64748b, #475569)",
  badge: "○",
};

const ROLE_LABELS: Record<string, string> = {
  admin: "Administrator",
  assessor: "Assessor",
  viewer: "Viewer",
};

const INDUSTRY_LABELS: Record<string, string> = {
  FS: "Financial Services",
  TECH: "Technology / SaaS",
  HEALTH: "Healthcare",
  CONST: "Construction / Manufacturing",
  GEN: "Generic / Internal",
};

/* ============================================================
   Main component
   ============================================================ */
const Settings: React.FC = () => {
  const { isAdmin, user } = useAuth();
  const [activeSection, setActiveSection] = useState("profile");
  const [entitlement, setEntitlement] = useState<Entitlement | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncLoading, setSyncLoading] = useState(false);
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      loadEntitlement();
    } else {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (user) {
      profileForm.setFieldsValue({
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
      });
    }
  }, [user]);

  /* ---- Data loaders ---- */
  const loadEntitlement = async () => {
    try {
      setLoading(true);
      const data = await apiService.getEntitlement();
      setEntitlement(data);
    } catch {
      message.error("Failed to load subscription information");
    } finally {
      setLoading(false);
    }
  };

  const handleSyncPlan = async () => {
    try {
      setSyncLoading(true);
      await loadEntitlement();
      message.success("Subscription plan synced");
    } catch {
      message.error("Failed to sync plan");
    } finally {
      setSyncLoading(false);
    }
  };

  /* ---- Handlers ---- */
  const handleCancelSubscription = () => {
    Modal.confirm({
      title: "Cancel Subscription",
      icon: <ExclamationCircleOutlined />,
      content:
        "Are you sure? This will deactivate your account immediately. AWS Marketplace handles all refunds.",
      okText: "Yes, Cancel",
      okType: "danger",
      cancelText: "Keep Subscription",
      onOk() {
        window.open(AWS_MARKETPLACE_URL, "_blank");
      },
    });
  };

  const handleUpdateProfile = async (values: any) => {
    try {
      setProfileLoading(true);
      const cognitoUser = await Auth.currentAuthenticatedUser();
      await Auth.updateUserAttributes(cognitoUser, {
        given_name: values.firstName,
        family_name: values.lastName,
      });
      try {
        const session = await Auth.currentSession();
        const sub = session.getIdToken().decodePayload()["sub"];
        if (sub) {
          await apiService.updateUser(sub, {
            firstName: values.firstName,
            lastName: values.lastName,
          });
        }
      } catch {
        /* non-critical */
      }
      await Auth.currentAuthenticatedUser({ bypassCache: true });
      message.success("Profile updated");
      window.location.reload();
    } catch (error: any) {
      message.error(error.message || "Failed to update profile");
    } finally {
      setProfileLoading(false);
    }
  };

  const handleChangePassword = async (values: any) => {
    try {
      setPasswordLoading(true);
      const cognitoUser = await Auth.currentAuthenticatedUser();
      await Auth.changePassword(
        cognitoUser,
        values.currentPassword,
        values.newPassword,
      );
      message.success("Password changed successfully");
      passwordForm.resetFields();
    } catch (error: any) {
      message.error(error.message || "Failed to change password");
    } finally {
      setPasswordLoading(false);
    }
  };

  /* ---- Computed values ---- */
  const isTrial =
    entitlement?.subscriptionPlan?.toLowerCase().includes("free") ||
    entitlement?.subscriptionPlan?.toLowerCase().includes("trial") ||
    entitlement?.status === "trial";

  const formatDate = (d?: string | null) =>
    d
      ? new Date(d).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : null;

  const planEndDate = formatDate(entitlement?.expirationDate);

  const calcDaysLeft = (d?: string | null): number | null => {
    if (!d) return null;
    const diff = new Date(d).getTime() - Date.now();
    return diff > 0 ? Math.ceil(diff / 86_400_000) : 0;
  };
  const daysLeft = isTrial ? calcDaysLeft(entitlement?.expirationDate) : null;

  const initials = (() => {
    const f = user?.firstName?.[0] ?? "";
    const l = user?.lastName?.[0] ?? "";
    return (f + l).toUpperCase() || (user?.email?.[0] ?? "U").toUpperCase();
  })();

  const planKey = entitlement?.subscriptionPlan ?? "Free Trial";
  const planStyle = PLAN_STYLES[planKey] ?? FALLBACK_PLAN_STYLE;

  const usagePct = entitlement
    ? Math.min(
        Math.round((entitlement.currentUsers / entitlement.maxUsers) * 100),
        100,
      )
    : 0;
  const usageWarn = usagePct >= 80;

  const displayName =
    user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : (user?.email?.split("@")[0] ?? "User");

  const navItems = [
    { id: "profile", label: "Profile", icon: <UserOutlined /> },
    { id: "security", label: "Security", icon: <LockOutlined /> },
    ...(isAdmin
      ? [{ id: "subscription", label: "Subscription", icon: <CloudOutlined /> }]
      : []),
  ];

  /* ============================================================
     RENDER
     ============================================================ */
  return (
    <div className="settings-root">
      {/* ---- Sidebar ---- */}
      <aside className="s-nav">
        <div className="s-nav-user">
          <div className="s-avatar">{initials}</div>
          <div>
            <div className="s-nav-user-name">{displayName}</div>
            <div className="s-nav-user-email">{user?.email}</div>
          </div>
        </div>
        <nav className="s-nav-list">
          {navItems.map((item) => (
            <button
              key={item.id}
              className={`s-nav-item${activeSection === item.id ? " active" : ""}`}
              onClick={() => setActiveSection(item.id)}
            >
              <span className="s-nav-icon">{item.icon}</span>
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
      </aside>

      {/* ---- Content (key triggers re-animation on tab switch) ---- */}
      <div key={activeSection} className="s-content">
        {/* =====================================================
            PROFILE
            ===================================================== */}
        {activeSection === "profile" && (
          <>
            <div className="s-content-header">
              <h2 className="s-section-heading">Profile</h2>
              <p className="s-section-desc">Manage your personal information</p>
            </div>

            <div className="s-card">
              <div className="s-card-header">
                <div className="s-card-icon blue">
                  <UserOutlined />
                </div>
                <div>
                  <div className="s-card-title">Personal Information</div>
                  <div className="s-card-subtitle">
                    Update your name and contact details
                  </div>
                </div>
              </div>
              <div className="s-card-body">
                <Form
                  form={profileForm}
                  layout="vertical"
                  onFinish={handleUpdateProfile}
                >
                  <div className="s-form-row">
                    <Form.Item
                      name="email"
                      label="Email Address"
                      rules={[
                        { required: true, message: "Email is required" },
                        {
                          type: "email",
                          message: "Please enter a valid email",
                        },
                      ]}
                    >
                      <Input prefix={<MailOutlined />} readOnly />
                    </Form.Item>
                    <Form.Item label="Industry">
                      <Input
                        prefix={<BankOutlined />}
                        value={
                          INDUSTRY_LABELS[user?.industryProfile ?? "GEN"] ??
                          user?.industryProfile ??
                          "—"
                        }
                        readOnly
                      />
                    </Form.Item>
                  </div>

                  <div className="s-form-row">
                    <Form.Item
                      name="firstName"
                      label="First Name"
                      rules={[{ required: true, message: "Required" }]}
                    >
                      <Input
                        prefix={<UserOutlined />}
                        placeholder="First name"
                      />
                    </Form.Item>
                    <Form.Item
                      name="lastName"
                      label="Last Name"
                      rules={[{ required: true, message: "Required" }]}
                    >
                      <Input placeholder="Last name" />
                    </Form.Item>
                  </div>

                  <div className="s-form-row" style={{ marginBottom: "10px" }}>
                    <Form.Item label="Role">
                      <Input
                        prefix={<SafetyCertificateOutlined />}
                        value={
                          ROLE_LABELS[user?.role ?? "viewer"] ??
                          user?.role ??
                          "—"
                        }
                        readOnly
                      />
                    </Form.Item>
                  </div>

                  <Form.Item>
                    <Button
                      className="s-btn-primary"
                      htmlType="submit"
                      loading={profileLoading}
                    >
                      Save Changes
                    </Button>
                  </Form.Item>
                </Form>
              </div>
            </div>
          </>
        )}

        {/* =====================================================
            SECURITY
            ===================================================== */}
        {activeSection === "security" && (
          <>
            <div className="s-content-header">
              <h2 className="s-section-heading">Security</h2>
              <p className="s-section-desc">
                Keep your account safe and secure
              </p>
            </div>

            <div className="s-card">
              <div className="s-card-header">
                <div className="s-card-icon indigo">
                  <LockOutlined />
                </div>
                <div>
                  <div className="s-card-title">Change Password</div>
                  <div className="s-card-subtitle">
                    Use a strong, unique password for your account
                  </div>
                </div>
              </div>
              <div className="s-card-body">
                <Form
                  form={passwordForm}
                  layout="vertical"
                  onFinish={handleChangePassword}
                >
                  <Form.Item
                    name="currentPassword"
                    label="Current Password"
                    rules={[{ required: true, message: "Required" }]}
                  >
                    <Password
                      prefix={<LockOutlined />}
                      placeholder="Current password"
                    />
                  </Form.Item>
                  <Form.Item
                    name="newPassword"
                    label="New Password"
                    rules={[
                      { required: true, message: "Required" },
                      { min: 8, message: "At least 8 characters" },
                    ]}
                  >
                    <Password placeholder="New password" />
                  </Form.Item>
                  <Form.Item
                    name="confirmPassword"
                    label="Confirm New Password"
                    dependencies={["newPassword"]}
                    rules={[
                      { required: true, message: "Required" },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue("newPassword") === value)
                            return Promise.resolve();
                          return Promise.reject(
                            new Error("Passwords do not match"),
                          );
                        },
                      }),
                    ]}
                  >
                    <Password placeholder="Confirm new password" />
                  </Form.Item>
                  <Form.Item>
                    <Button
                      className="s-btn-primary"
                      htmlType="submit"
                      loading={passwordLoading}
                    >
                      Update Password
                    </Button>
                  </Form.Item>
                </Form>
              </div>
            </div>
          </>
        )}

        {/* =====================================================
            SUBSCRIPTION  (admin only)
            ===================================================== */}
        {activeSection === "subscription" && isAdmin && (
          <>
            <div className="s-content-header">
              <h2 className="s-section-heading">Subscription</h2>
              <p className="s-section-desc">Manage your plan and billing</p>
            </div>

            {/* ---- Skeleton while loading ---- */}
            {loading && (
              <>
                <div className="s-skeleton-plan-card">
                  <div className="s-skeleton-plan-header">
                    <div className="s-skeleton-block" style={{ width: "120px", height: "22px" }} />
                    <div className="s-skeleton-block" style={{ width: "80px", height: "16px" }} />
                    <div className="s-skeleton-block" style={{ width: "160px", height: "14px" }} />
                  </div>
                  <div className="s-skeleton-plan-body">
                    <div className="s-skeleton-block" style={{ height: "14px", width: "60%" }} />
                    <div className="s-skeleton-block" style={{ height: "8px", borderRadius: "4px" }} />
                    <div className="s-skeleton-block" style={{ height: "12px", width: "40%" }} />
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginTop: "8px" }}>
                      {[1,2,3,4].map(i => (
                        <div key={i} style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                          <div className="s-skeleton-block" style={{ height: "11px", width: "50%" }} />
                          <div className="s-skeleton-block" style={{ height: "14px", width: "70%" }} />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="s-skeleton-aws-card">
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px", flex: 1 }}>
                    <div className="s-skeleton-block" style={{ height: "16px", width: "140px" }} />
                    <div className="s-skeleton-block" style={{ height: "12px", width: "80%" }} />
                  </div>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <div className="s-skeleton-block" style={{ height: "36px", width: "100px", borderRadius: "8px" }} />
                    <div className="s-skeleton-block" style={{ height: "36px", width: "90px", borderRadius: "8px" }} />
                  </div>
                </div>
              </>
            )}

            {/* Trial banner */}
            {!loading && isTrial && (
              <div className="s-trial-banner">
                <div className="s-trial-icon">⭐</div>
                <div>
                  <div className="s-trial-title">Free Trial Active</div>
                  <div className="s-trial-desc">
                    {daysLeft !== null
                      ? `${daysLeft} day${daysLeft !== 1 ? "s" : ""} remaining${
                          planEndDate ? ` — expires ${planEndDate}` : ""
                        }`
                      : planEndDate
                        ? `Expires ${planEndDate}`
                        : "Enjoy all features during your trial"}
                  </div>
                </div>
              </div>
            )}

            {/* Plan card + AWS + Danger — hidden while loading */}
            {!loading && (<><div className="s-plan-card">
              <div
                className="s-plan-header"
                style={{ background: planStyle.gradient }}
              >
                <div className="s-plan-tier-badge">
                  <span>{planStyle.badge}</span>
                  <span>{planKey}</span>
                </div>
                <div className="s-plan-name">{planKey}</div>
                <div className="s-plan-tagline">
                  {entitlement?.maxUsers ?? 0} seats included · 1 admin seat
                </div>
              </div>

              <div className="s-plan-body">
                {/* Seats usage */}
                <div className="s-usage-row">
                  <span className="s-usage-label">Seats Used</span>
                  <span className="s-usage-count">
                    {entitlement?.currentUsers ?? 0} /{" "}
                    {entitlement?.maxUsers ?? 0}
                  </span>
                </div>
                <div className="s-usage-track">
                  <div
                    className={`s-usage-fill${usageWarn ? " warn" : ""}`}
                    style={{ width: `${usagePct}%` }}
                  />
                </div>
                <div className="s-usage-remaining">
                  {entitlement
                    ? `${entitlement.maxUsers - entitlement.currentUsers} seat${
                        entitlement.maxUsers - entitlement.currentUsers !== 1
                          ? "s"
                          : ""
                      } available`
                    : "—"}
                </div>

                {/* Info grid */}
                <div className="s-info-grid">
                  <div className="s-info-item">
                    <span className="s-info-label">Status</span>
                    <span
                      className={`s-info-value${
                        entitlement?.status === "active" ? " active" : ""
                      }`}
                    >
                      {entitlement?.status === "active"
                        ? "Active"
                        : (entitlement?.status ?? "—")}
                    </span>
                  </div>
                  <div className="s-info-item">
                    <span className="s-info-label">
                      {isTrial ? "Trial Ends" : "Plan Ends"}
                    </span>
                    <span className="s-info-value">{planEndDate ?? "—"}</span>
                  </div>
                  <div className="s-info-item">
                    <span className="s-info-label">Plan Tier</span>
                    <span className="s-info-value">{planKey}</span>
                  </div>
                  <div className="s-info-item">
                    <span className="s-info-label">Last Synced</span>
                    <span className="s-info-value">
                      {entitlement?.lastSyncAt
                        ? formatDate(entitlement.lastSyncAt)
                        : "—"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* AWS Marketplace */}
            <div className="s-aws-card">
              <div className="s-aws-info">
                <div className="s-aws-title">AWS Marketplace</div>
                <div className="s-aws-desc">
                  Your subscription is managed through AWS Marketplace. Plan
                  upgrades and billing changes sync automatically to your
                  account.
                </div>
              </div>
              <div className="s-aws-actions">
                <Button
                  className="s-btn-secondary"
                  icon={<SyncOutlined />}
                  onClick={handleSyncPlan}
                  loading={syncLoading}
                >
                  Sync Plan
                </Button>
                <Button
                  className="s-btn-primary"
                  icon={<LinkOutlined />}
                  onClick={() => window.open(AWS_MARKETPLACE_URL, "_blank")}
                >
                  Manage
                </Button>
              </div>
            </div>

            {/* Danger zone */}
            <div className="s-card">
              <div className="s-card-header">
                <div className="s-card-icon red">
                  <ExclamationCircleOutlined />
                </div>
                <div>
                  <div className="s-card-title">Cancel Subscription</div>
                  <div className="s-card-subtitle">
                    Deactivates your account immediately. AWS Marketplace
                    handles refunds.
                  </div>
                </div>
              </div>
              <div className="s-card-body">
                <Button
                  className="s-btn-danger"
                  onClick={handleCancelSubscription}
                >
                  Cancel via AWS Marketplace
                </Button>
              </div>
            </div>
          </>)}
          </>
        )}
      </div>
    </div>
  );
};

export default Settings;
